import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Card, Chip, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';

import { SectionTabs, withCount } from '~/components/common/SectionTabs';
import { SectionTitle } from '~/components/common/SectionTitle';
import { ElevatorViolationItem } from '~/components/compliance/detailPage/ElevatorViolationItem';
import { ViolationSkeleton } from '~/components/compliance/ViolationSkeleton';
import type { Violation } from '~/data/schemas/car';
import type { API_Compliance } from '~/data/schemas/compliance';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { BUTTON_SX } from '~/theme';

const isResolved = (violation: Violation) => violation.status.toLowerCase() === 'resolved';

/**
 * How wide a violation row is allowed to get. ~75 characters of `body2` description is
 * where a line stops being comfortable to read; past this the row keeps growing but
 * nothing in it does. The card itself still spans the tab — only its rows are capped.
 */
const VIOLATION_MAX_WIDTH = 900;

function ViolationRows({
  violations,
  compliance,
  onEditViolation,
  onNoteAdded,
  isLoading,
  emptyLabel,
}: {
  violations: Violation[];
  compliance: API_Compliance;
  onEditViolation: ((violationId: string) => void) | null;
  onNoteAdded: () => void;
  isLoading: boolean;
  emptyLabel: string;
}) {
  if (isLoading) {
    return <ViolationSkeleton />;
  }

  if (violations.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Box sx={{ maxWidth: VIOLATION_MAX_WIDTH }}>
      {violations.map((violation) => (
        <ElevatorViolationItem
          key={violation.id}
          violation={violation}
          certificateCarId={compliance.carId}
          certificateCarName={compliance.unitName}
          onEdit={onEditViolation ? () => onEditViolation(violation.id) : null}
          onNoteAdded={onNoteAdded}
        />
      ))}
    </Box>
  );
}

/**
 * The car's violations, open and resolved, as two views with their counts — the
 * drawer's "Show Resolved" toggle said nothing about how many were behind it. Each row
 * carries its own notes, open on the page rather than behind the drawer's modal.
 */
export function ElevatorViolationsCard({
  compliance,
  violations,
  isLoading,
  onAddViolation,
  onEditViolation,
  onNoteAdded,
}: {
  compliance: API_Compliance;
  violations: Violation[];
  isLoading: boolean;
  /** `null` for a reader who can't record violations, which removes the button. */
  onAddViolation: (() => void) | null;
  /** `null` for a reader who can't edit one, which removes the row's button. */
  onEditViolation: ((violationId: string) => void) | null;
  /** Refreshes the list so note counts follow what was just written. */
  onNoteAdded: () => void;
}) {
  // en-GB calls these Defects, not Violations. The card is the only place a UK
  // consultant sees them named on this page, so every one of its labels has to follow
  // the term — the heading, both empty states and the Add button. (AMP-741)
  const terms = useLocaleTerms();
  const open = violations.filter((violation) => !isResolved(violation));
  const resolved = violations.filter(isResolved);
  // Undefined while loading, so the chips render as nothing rather than a zero
  // that becomes a number a moment later.
  const count = (rows: Violation[]) => (isLoading ? undefined : rows.length);

  const nextDue = compliance.nextViolationDue
    ? format(parseISO(compliance.nextViolationDue), 'MMM d, yyyy')
    : null;

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
        <SectionTitle>{terms.violations}</SectionTitle>
        {nextDue ? (
          <Typography variant="caption" color="text.secondary">
            Next due {nextDue}
          </Typography>
        ) : null}
        {compliance.violationsWithoutUpdates > 0 ? (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`${compliance.violationsWithoutUpdates} with no update in 2 weeks`}
          />
        ) : null}
      </Stack>

      <SectionTabs
        ariaLabel={`${terms.violation} views`}
        tabs={[
          {
            value: 'open',
            label: withCount('Open', count(open)),
            render: () => (
              <>
                {onAddViolation ? (
                  <Box mb={2}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={onAddViolation}
                      sx={BUTTON_SX}
                    >
                      Add {terms.violation.toLowerCase()}
                    </Button>
                  </Box>
                ) : null}
                <ViolationRows
                  violations={open}
                  compliance={compliance}
                  onEditViolation={onEditViolation}
                  onNoteAdded={onNoteAdded}
                  isLoading={isLoading}
                  emptyLabel={`No open ${terms.violations.toLowerCase()} on this ${terms.unit.toLowerCase()}.`}
                />
              </>
            ),
          },
          {
            value: 'resolved',
            label: withCount('Resolved', count(resolved)),
            render: () => (
              <ViolationRows
                violations={resolved}
                compliance={compliance}
                onEditViolation={onEditViolation}
                onNoteAdded={onNoteAdded}
                isLoading={isLoading}
                emptyLabel={`Nothing has been resolved on this ${terms.unit.toLowerCase()} yet.`}
              />
            ),
          },
        ]}
      />
    </Card>
  );
}
